"use client";

import { TrendingUp } from "lucide-react";
import {
	Area,
	AreaChart,
	CartesianGrid,
	XAxis,
	YAxis,
	ResponsiveContainer,
} from "recharts";
import { useState, useEffect } from "react";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Bar, BarChart, Legend } from "recharts";
import type { Employee } from "@/interfaces/dashboard";
import {
	type ChartConfig,
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
} from "@/components/ui/chart";

interface EmployeeData {
	yearly: number;
	totalEmployees: number;
}

const chartConfig = {
	totalEmployees: {
		label: "ຈຳນວນພະນັກງານ",
		color: "#008000",
	},
} satisfies ChartConfig;

export function Employees({ data }: { data: EmployeeData[] }) {
	const [chartData, setChartData] = useState<EmployeeData[]>([]);
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		setIsLoading(true);
		if (Array.isArray(data) && data.length > 0) {
			const sortedData = [...data].sort((a, b) => a.yearly - b.yearly);

			setChartData(sortedData);
		}
		setIsLoading(false);
	}, [data]);

	const formatValue = (value: number) => {
		if (value === 0) return "0 ຄົນ";
		if (value >= 1000000) return `${(value / 1000000).toFixed(0)} ລ້ານ ຄົນ`;
		if (value >= 1000) return `${(value / 1000).toFixed(0)} ພັນ ຄົນ`;
		return `${value} ຄົນ`;
	};

	if (isLoading)
		return (
			<Card>
				<CardContent>ກຳລັງໂຫຼດ...</CardContent>
			</Card>
		);
	if (!chartData.length)
		return (
			<Card>
				<CardContent>ບໍ່ມີຂໍ້ມູນ</CardContent>
			</Card>
		);

	return (
		<Card>
			<CardHeader>
				<CardTitle>ລາຍງານຈຳນວນພະນັກງານ</CardTitle>
				<CardDescription>ລາຍລະອຽດລາຍງານຈຳນວນພະນັກງານ</CardDescription>
			</CardHeader>
			<CardContent>
				<ChartContainer config={chartConfig}>
					<ResponsiveContainer width="100%" height={400}>
						<AreaChart
							data={chartData}
							margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
						>
							<CartesianGrid strokeDasharray="3 3" />
							<XAxis dataKey="yearly" tickLine={false} axisLine={true} />
							<YAxis
								orientation="left"
								tickFormatter={formatValue}
								tickLine={false}
								axisLine={true}
							/>
							<ChartTooltip
								content={({ active, payload, label }) => {
									if (active && payload?.[0]) {
										return (
											<div className="p-2 border rounded-lg shadow-lg bg-background">
												<p className="font-medium">{`ປີ ${label}`}</p>
												<p className="text-primary">
													{formatValue(payload[0].value as number)}
												</p>
											</div>
										);
									}
									return null;
								}}
							/>
							<defs>
								<linearGradient id="colorEmployees" x1="0" y1="0" x2="0" y2="1">
									<stop
										offset="5%"
										stopColor={chartConfig.totalEmployees.color}
										stopOpacity={0.8}
									/>
									<stop
										offset="95%"
										stopColor={chartConfig.totalEmployees.color}
										stopOpacity={0.1}
									/>
								</linearGradient>
							</defs>
							<Area
								type="monotone"
								dataKey="totalEmployees"
								stroke={chartConfig.totalEmployees.color}
								fill="url(#colorEmployees)"
								fillOpacity={0.4}
							/>
						</AreaChart>
					</ResponsiveContainer>
				</ChartContainer>
			</CardContent>
		</Card>
	);
}
